import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Bell, CheckCircle2, Clock, Handshake, TrendingUp, Wallet,
} from 'lucide-react';
import { useAppSelector } from '../../hooks/redux';
import PageHero from '../../components/PageHero';

const highlightIcons = [Wallet, Handshake, Bell];

export default function FundraisingPage() {
  const { fundraisingProcedure, countryLabel, hero, currency } = useAppSelector((s) => s.geo.content);

  return (
    <>
      <PageHero
        badge={`${countryLabel} · Fundraising`}
        title={fundraisingProcedure.title}
        subtitle={fundraisingProcedure.summary}
      />
      <section style={{ padding: '32px 0 0', background: '#FFFBF8' }}>
        <div className="container-wide">
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 24,
              padding: '36px 28px',
              background: 'linear-gradient(125deg, #2D0A1A 0%, #880E4F 45%, #C2185B 100%)',
              color: 'white',
            }}
          >
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
            <div style={{ position: 'relative', zIndex: 1, maxWidth: 720 }}>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.7 }}>
                {hero.comboHighlight} · {currency}
              </p>
              <h2 style={{ margin: '10px 0 12px', fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Raise with a rating already in hand
              </h2>
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.7, opacity: 0.88, maxWidth: 580 }}>
                Fundraising on Micus is not a separate product. Your credit pack opens the raise track, partners see a clearer story, and you spend less calendar time between readiness and capital.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 22 }}>
                <Link to="/#start" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderRadius: 999, background: 'white', color: '#C2185B', fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>
                  Start rating & raise <ArrowRight size={16} />
                </Link>
                <Link to="/credit-rating" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderRadius: 999, border: '1.5px solid rgba(255,255,255,0.4)', color: 'white', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
                  See rating standards
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 0', background: '#FFFBF8' }}>
        <div className="container-wide">
          <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C2185B' }}>
            Procedure
          </p>
          <h2 style={{ margin: '8px 0 24px', fontSize: 28, fontWeight: 900 }}>Step by step</h2>
          <div style={{ display: 'grid', gap: 14, maxWidth: 900 }}>
            {fundraisingProcedure.steps.map((step, i) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                style={{
                  background: 'white',
                  border: '1px solid #F0C8D8',
                  borderRadius: 16,
                  padding: '18px 22px',
                  display: 'flex',
                  gap: 16,
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    minWidth: 42,
                    height: 42,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg,#C2185B,#E91E63)',
                    color: 'white',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 900,
                    fontSize: 13,
                  }}
                >
                  0{i + 1}
                </span>
                <div>
                  <p style={{ margin: 0, fontSize: 15.5, color: '#1A0A10', fontWeight: 700, lineHeight: 1.55 }}>{step}</p>
                  {i === 1 && (
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6B3A52' }}>
                      Rating runs inside fundraising — your raise track unlocks when the pack is ready.
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 0', background: 'white', borderTop: '1px solid #F0C8D8' }}>
        <div className="container-wide">
          <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C2185B' }}>
            Why it is faster
          </p>
          <h2 style={{ margin: '8px 0 24px', fontSize: 28, fontWeight: 900 }}>Built for shorter raise cycles</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {fundraisingProcedure.highlights.map((h, i) => {
              const Icon = highlightIcons[i % highlightIcons.length];
              return (
                <div key={h.title} style={{ background: '#FFFBF8', border: '1px solid #F0C8D8', borderRadius: 18, padding: 24 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#C2185B,#E91E63)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <Icon size={18} color="#fff" />
                  </div>
                  <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800 }}>{h.title}</h3>
                  <p style={{ margin: 0, fontSize: 14, color: '#6B3A52', lineHeight: 1.65 }}>{h.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 0 72px', background: '#FFF0F5' }}>
        <div className="container-wide" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, alignItems: 'start' }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C2185B' }}>
              Outcomes
            </p>
            <h2 style={{ margin: '8px 0 16px', fontSize: 26, fontWeight: 900 }}>What you walk away with</h2>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
              {fundraisingProcedure.outcomes.map((o) => (
                <li key={o} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'white', border: '1px solid #F0C8D8', borderRadius: 14, padding: '14px 16px' }}>
                  <CheckCircle2 size={18} color="#C2185B" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 14.5, color: '#4A2535', lineHeight: 1.55 }}>{o}</span>
                </li>
              ))}
            </ul>
          </div>
          <div style={{ background: 'white', borderRadius: 22, border: '1px solid #F0C8D8', padding: 28 }}>
            <TrendingUp size={22} color="#C2185B" />
            <h3 style={{ margin: '12px 0 8px', fontSize: 20, fontWeight: 900 }}>From need → funds</h3>
            <p style={{ margin: '0 0 18px', fontSize: 14.5, color: '#6B3A52', lineHeight: 1.65 }}>
              Micus is the system of record for requirement intake, country-aware credit, partner connection, and notifications until money arrives — with partners accountable for final credit and disbursement.
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              {[
                { icon: Clock, text: 'SLA clocks on disbursement milestones' },
                { icon: Bell, text: 'Alerts when you or a partner must act' },
                { icon: Handshake, text: 'Active lead count you can see anytime' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13.5, color: '#4A2535' }}>
                  <Icon size={16} color="#C2185B" /> {text}
                </div>
              ))}
            </div>
            <Link
              to="/how-it-works"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 22, color: '#C2185B', fontWeight: 800, fontSize: 14 }}
            >
              See AI at every stage <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
